import React, { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, LinearProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

export default function ExcelDropzone({ onUploadComplete, selectedDate }) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadFile = useCallback(
    (file) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const url = 'https://stage-apiplatform.finkraftai.com/api/misc/bookingdata/client/upload';
        xhr.open('POST', url);
        xhr.responseType = 'json';

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const pct = Math.round((event.loaded / event.total) * 100);
            setProgress(pct);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response);
          } else {
            reject(new Error('Upload failed'));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        const form = new FormData();
        form.append('file', file);
        // Send local-offset ISO to avoid UTC shifting previous/next day
        const isoDate = dayjs(selectedDate || dayjs())
          .startOf('day')
          .format('YYYY-MM-DDTHH:mm:ssZ');
        form.append('date', isoDate);
        xhr.send(form);
      });
    },
    [selectedDate]
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles && acceptedFiles[0];
      if (!file) return;
      setIsUploading(true);
      setProgress(0);
      try {
        const serverResponse = await uploadFile(file);
        setIsUploading(false);
        setProgress(100);
        onUploadComplete?.(serverResponse || { file_name: file.name });
      } catch (e) {
        setIsUploading(false);
        // fallback: still show completion and notify parent with local info
        onUploadComplete?.({ file_name: file.name });
      }
    },
    [uploadFile, onUploadComplete]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          p: 6,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'secondary.main',
          bgcolor: isDragActive ? 'rgba(5,165,183,0.06)' : 'background.paper',
          color: 'text.secondary',
          textAlign: 'center',
          borderRadius: 2,
          transition: 'all .2s ease',
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        <UploadFileIcon
          color={isDragActive ? 'primary' : 'disabled'}
          sx={{ fontSize: 40, mb: 1 }}
        />
        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
          Drop your Excel file here
        </Typography>
        <Typography variant="body2">or click to select .xlsx / .xls</Typography>
      </Box>
      {isUploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
            Uploading... {progress}%
          </Typography>
        </Box>
      )}
    </Box>
  );
}
